import { DATA_SERVICE_BASE_URL } from '../ApiService';
import { AI_SERVICE_BASE_URL } from '../AiApiService';
import { fetchWithAuth } from '../AuthService';

const STUDY_GUIDE_BASE = `${DATA_SERVICE_BASE_URL}/api/student/study-guide`;

/**
 * Study Guide API — user-facing endpoints for the personalized study guide.
 * No admin role required; userId is inferred from JWT.
 */
export const StudyGuideApi = {

  /**
   * Get the full study guide (mastery, recommendations, deadlines, tutor summary).
   * @param {number} [endeavorId] - Optional endeavor/course filter
   */
  getStudyGuide: async (endeavorId) => {
    const params = endeavorId ? `?endeavorId=${endeavorId}` : '';
    const response = await fetchWithAuth(`${STUDY_GUIDE_BASE}${params}`);
    if (!response.ok) throw new Error(`Study guide failed: ${response.status}`);
    return response.json();
  },

  /**
   * Force-refresh recommendations and return the updated study guide.
   * @param {number} [endeavorId] - Optional endeavor/course filter
   */
  refreshRecommendations: async (endeavorId) => {
    const params = endeavorId ? `?endeavorId=${endeavorId}` : '';
    const response = await fetchWithAuth(`${STUDY_GUIDE_BASE}/refresh${params}`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error(`Refresh recommendations failed: ${response.status}`);
    return response.json();
  },

  /**
   * Get detailed view of a single concept/topic.
   * @param {string} slug - Concept slug
   * @param {number} [endeavorId] - Optional endeavor/course filter
   */
  getTopicDetail: async (slug, endeavorId) => {
    const params = endeavorId ? `?endeavorId=${endeavorId}` : '';
    const response = await fetchWithAuth(`${STUDY_GUIDE_BASE}/topic/${encodeURIComponent(slug)}${params}`);
    if (!response.ok) throw new Error(`Topic detail failed: ${response.status}`);
    return response.json();
  },

  /**
   * Mark a recommendation as completed.
   * @param {number} id - Recommendation ID
   */
  completeRecommendation: async (id) => {
    const response = await fetchWithAuth(`${STUDY_GUIDE_BASE}/recommendations/${id}/complete`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error(`Complete recommendation failed: ${response.status}`);
    return response;
  },

  /**
   * Mark a recommendation as dismissed.
   * @param {number} id - Recommendation ID
   */
  dismissRecommendation: async (id) => {
    const response = await fetchWithAuth(`${STUDY_GUIDE_BASE}/recommendations/${id}/dismiss`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error(`Dismiss recommendation failed: ${response.status}`);
    return response;
  },

  /**
   * Get the user's courses/endeavors for the course picker.
   */
  getCourses: async () => {
    const response = await fetchWithAuth(`${STUDY_GUIDE_BASE}/courses`);
    if (!response.ok) throw new Error(`Get courses failed: ${response.status}`);
    return response.json();
  },

  /**
   * Generate a tutor summary via AI-Service (proxied).
   * @param {object} data - { masteryData, recommendations, deadlines, recentActivity }
   */
  generateTutorSummary: async (data) => {
    const response = await fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/tutor/generate-summary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`Generate tutor summary failed: ${response.status}`);
    return response.json();
  },

  /**
   * Save a generated tutor summary to the database.
   * @param {object} data - { endeavorId, summaryText, contextHash }
   */
  saveTutorSummary: async (data) => {
    const response = await fetchWithAuth(`${STUDY_GUIDE_BASE}/tutor-summary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`Save tutor summary failed: ${response.status}`);
    return response.json();
  },
};
